import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useVersion } from "@/lib/VersionContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { resolveBackendFileUrl } from "@/lib/fileUrls";
import { APP_VERSION, APP_RELEASE_DATE, LATEST_RELEASE_LABEL } from "@/version";
import {
  House, SignOut, Gear, X, Buildings, UsersThree, Lifebuoy, UsersFour, Package, Target, FileText, Folders, ChartBar, GearSix, ChatCircle, CalendarCheck, Heart, Lightning
} from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// module: maps to the permission module key. If omitted, always visible.
const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: House,
    tip: "Your personal home base — pending approvals, renewals at risk, open tickets, and quick links to recent work." },
  { path: "/projects", label: "Projects", icon: Folders, module: "projects",
    tip: "Delivery projects — tasks, milestones, assignments, risks, and status reporting." },
  { path: "/support", label: "Support", icon: Lifebuoy, module: "support",
    tip: "Support tickets across all clients — SLA timers, assignment queue, inbound email parsing." },
  { path: "/clients", label: "Clients", icon: Buildings, internalOnly: true, module: "clients",
    tip: "Your Eyvo client roster — tenant settings, eBA users, meetings, contracts, and renewal dates." },
  // Pulse UI consolidation ruling (2026-08-18): removed the standalone
  // "EBA 2.0 Tenants" global nav item. EBA provisioning is client-scoped —
  // it lives at Clients → [Client] → EBA Provisioning. The old /eba2 route
  // still resolves (App.js) to a redirect notice for any existing bookmark.
  { type: "divider", internalOnly: true, modules: ["leads", "campaigns"] },
  { type: "label", label: "Marketing", internalOnly: true, modules: ["leads", "campaigns"] },
  { path: "/crm/leads", label: "Sales Leads", icon: Target, internalOnly: true, module: "leads",
    tip: "Inbound and outbound sales pipeline — stages, owner assignment, activity log, pending-approval queue for website leads." },
  { path: "/crm/replies", label: "Replies Inbox", icon: ChatCircle, internalOnly: true, module: "leads",
    tip: "Every email reply received from a lead — Mark handled to clear, Open in CRM to jump to the lead." },
  { path: "/crm/booking-checks", label: "Booking Check-ins", icon: CalendarCheck, internalOnly: true, module: "leads",
    tip: "Leads who clicked the meeting link but haven't been confirmed yet. Yes → keep paused. No → resume the drip from the same step." },
  { path: "/crm/drip-health", label: "Drip Health", icon: Heart, internalOnly: true, module: "leads",
    tip: "Lead-centric view of who's NOT being nurtured right now — no active drip, paused enrolments, lead-level paused, all-errored, no-email-in-N-days." },
  { path: "/marketing", label: "Pulse Campaigns", icon: UsersFour, internalOnly: true, module: "campaigns",
    tip: "The email marketing hub — campaigns, automated workflows, segments, contacts, imports, reports and suppression." },
  { path: "/marketing/audience", label: "Audience & Cleanup", icon: UsersThree, internalOnly: true, module: "campaigns",
    tip: "Every marketing contact by what it is and why — review decisions, holds, Sales-owned, and cleanup: remove bad (undeliverable) addresses, delete garbage and Eyvo staff test data." },
  { path: "/marketing/activity", label: "Email Activity", icon: Lightning, internalOnly: true, module: "campaigns",
    tip: "Global feed of every email going out of Pulse — drip workflows + broadcast campaigns. Filter by lead, workflow, status, step, date. Auto-refreshes." },
  { type: "divider", internalOnly: true, modules: ["sales"] },
  { type: "label", label: "Sales", internalOnly: true, modules: ["sales"] },
  { path: "/quotes", label: "Proposals", icon: FileText, internalOnly: true, module: "sales",
    tip: "Proposals & quotes — draft, send, track e-sign status, convert won proposals into projects." },
  { path: "/mgmt/products", label: "Products", icon: Package, internalOnly: true, module: "products",
    tip: "Products and SKUs that show up on proposals and quotes — pricing, descriptions, categories." },
  { type: "divider", internalOnly: true },
  { path: "/admin", label: "System Admin", icon: GearSix, internalOnly: true, adminOnly: true,
    tip: "Platform settings — users & roles, SLA configuration, email templates, integrations, audit log." },
];

export default function Sidebar({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, canSee, hasRole } = useAuth();
  const { isStale, live } = useVersion();
  // 2026-07 client-logo repair sprint — some tenant.logo_url values point
  // at blobs that no longer exist; fall back to the plain "Pulse" wordmark
  // instead of a broken-image icon.
  const [tenantLogoBroken, setTenantLogoBroken] = useState(false);

  // Live unread-replies count for the badge dot on /crm/replies. Polled
  // every 60s so we don't over-burden the API but the marketer still
  // notices a fresh reply within a minute. We deliberately fail silent
  // on error — the dot just stays on whatever it last knew.
  const [unreadReplies, setUnreadReplies] = useState(0);
  // Session 73 — pending booking-check prompts (leads who clicked the
  // meeting link but haven't been confirmed Yes/No yet). Same polling
  // cadence, same fail-silent contract.
  const [pendingBookingChecks, setPendingBookingChecks] = useState(0);
  // v1.57 — SQL Server host + database name (passwords masked
  // server-side). Surfaced directly under the Version pill so the
  // operator can see which DB this pod is actually talking to without
  // navigating to /admin/db-connections. Internal-only, same gate as
  // the rest of the diagnostic info in the sidebar.
  const [dbConn, setDbConn] = useState(null);
  const isInternal = user?.user_type === "internal";

  useEffect(() => {
    if (!isInternal) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/users/me/db-connections");
        if (!cancelled) setDbConn(r.data);
      } catch { /* fail-silent — diagnostic only */ }
    })();
    return () => { cancelled = true; };
  }, [isInternal]);

  useEffect(() => {
    if (!isInternal) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/crm/replies?status=open&limit=1");
        if (!cancelled) setUnreadReplies(r.data?.counts?.open || 0);
      } catch { /* swallow — leave existing dot state */ }
      try {
        const b = await api.get("/crm/booking-checks/pending-count");
        if (!cancelled) setPendingBookingChecks(b.data?.count || 0);
      } catch { /* fail-silent */ }
    };
    tick();
    const id = setInterval(tick, 60_000);
    // Allow other pages (e.g. BookingCheckInsPage) to nudge an
    // immediate badge refresh after they mutate state — so the
    // count goes from N → N-1 the instant the operator clicks
    // YES/NO, not on the next 60s tick.
    const onBookingChange = () => { tick(); };
    window.addEventListener("eyvo:booking-checks-changed", onBookingChange);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("eyvo:booking-checks-changed", onBookingChange);
    };
  }, [isInternal]);

  const handleNav = (path) => {
    navigate(path);
    onClose?.();
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isAdmin = hasRole("platform_admin") || hasRole("delivery_admin");

  const visibleItems = navItems.filter(item => {
    // Internal-only filter
    if (item.internalOnly && !isInternal) return false;
    // Admin-only filter
    if (item.adminOnly && !["platform_admin", "delivery_admin"].includes(user?.role)) return false;
    // Module permission filter for nav links
    if (item.module && isInternal) return canSee(item.module);
    // Section dividers/labels: show only if at least one of their modules is visible
    if (item.modules && isInternal) return item.modules.some(m => canSee(m));
    return true;
  });

  // Highlight only the MOST specific matching item, so /marketing/audience
  // lights "Audience & Cleanup" and not also "Pulse Campaigns".
  const activePath = visibleItems
    .filter((i) => i.path && (location.pathname === i.path
      || (i.path !== "/dashboard" && location.pathname.startsWith(i.path + "/"))))
    .reduce((best, i) => (!best || i.path.length > best.length ? i.path : best), null);

  return (
    <div className="w-64 h-full flex flex-col" style={{ backgroundColor: "var(--theme-sidebar-bg, #FFFFFF)", borderRight: "1px solid var(--theme-sidebar-border, #E5E5E5)" }} data-testid="sidebar">
      <div className="h-14 flex items-center justify-between px-5" style={{ borderBottom: "1px solid var(--theme-sidebar-border, #E5E5E5)" }}>
        <div className="flex items-center gap-2 cursor-pointer overflow-hidden" onClick={() => handleNav("/dashboard")}>
          <img src="/eyvo-logo.png" alt="Eyvo" className="h-6 w-auto flex-shrink-0" data-testid="sidebar-logo" />
          {/* Session 68 pt3 — when a client user is signed in AND
              their tenant has a logo on file, swap the "Pulse"
              wordmark for their company logo so the sidebar instantly
              feels co-branded. We keep the Eyvo lockup on the left so
              the partnership is visible. Falls back to the Pulse
              wordmark when the tenant hasn't uploaded yet. */}
          {user?.user_type === "client" && user?.tenant?.logo_url && !tenantLogoBroken ? (
            <>
              <span className="text-xs font-black tracking-tight flex-shrink-0" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", color: "var(--theme-sidebar-text, #1a2b6d)" }}>Pulse</span>
              <span className="text-[#A3A3A3] flex-shrink-0">·</span>
              <img
                src={resolveBackendFileUrl(user.tenant.logo_url)}
                alt={user.tenant.name || "Client logo"}
                className="h-5 max-w-[110px] object-contain"
                onError={() => setTenantLogoBroken(true)}
                data-testid="sidebar-client-logo"
              />
            </>
          ) : (
            <span className="text-sm font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif", color: "var(--theme-sidebar-text, #1a2b6d)" }}>Pulse</span>
          )}
        </div>
        <button onClick={onClose} className="md:hidden p-1" data-testid="sidebar-close">
          <X size={18} style={{ color: "var(--theme-sidebar-text, #525252)" }} />
        </button>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        <TooltipProvider delayDuration={250}>
        {visibleItems.map((item, idx) => {
          if (item.type === "divider") return <div key={`div-${idx}`} className="my-3" style={{ borderTop: "1px solid var(--theme-sidebar-border, #E5E5E5)" }} />;
          if (item.type === "label") return <p key={`lbl-${idx}`} className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--theme-sidebar-heading, #A3A3A3)" }}>{item.label}</p>;
          const { path, label, icon: Icon, tip } = item;
          const isActive = path === activePath;
          // Render an unread-counter dot on the Replies Inbox row.
          // Only shown when isInternal AND there's at least one open reply.
          const showRepliesBadge = path === "/crm/replies" && isInternal && unreadReplies > 0;
          const showBookingBadge = path === "/crm/booking-checks" && isInternal && pendingBookingChecks > 0;
          const btn = (
            <button
              onClick={() => handleNav(path)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-sm border-l-2"
              style={isActive
                ? { backgroundColor: "var(--theme-sidebar-active-bg)", color: "var(--theme-sidebar-active-text, var(--theme-primary, #0055FF))", borderLeftColor: "var(--theme-primary, #0055FF)" }
                : { color: "var(--theme-sidebar-text, #525252)", borderColor: "transparent" }}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={18} weight={isActive ? "fill" : "regular"} />
              <span className="flex-1 text-left">{label}</span>
              {showRepliesBadge && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-[#DC2626] text-white tabular-nums"
                  data-testid="replies-unread-badge"
                  aria-label={`${unreadReplies} unread reply${unreadReplies === 1 ? "" : "ies"}`}
                >
                  {unreadReplies > 99 ? "99+" : unreadReplies}
                </span>
              )}
              {showBookingBadge && (
                <span
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full bg-[#0055FF] text-white tabular-nums"
                  data-testid="booking-checks-pending-badge"
                  aria-label={`${pendingBookingChecks} pending booking ${pendingBookingChecks === 1 ? "check" : "checks"}`}
                >
                  {pendingBookingChecks > 99 ? "99+" : pendingBookingChecks}
                </span>
              )}
            </button>
          );
          return tip ? (
            <Tooltip key={path}>
              <TooltipTrigger asChild>{btn}</TooltipTrigger>
              <TooltipContent
                side="right" align="start" sideOffset={10}
                className="w-64 bg-[#0A0A0A] text-white border-[#0A0A0A] p-2.5 text-[11px] leading-snug"
              >
                <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">{label}</span>
                {tip}
              </TooltipContent>
            </Tooltip>
          ) : btn;
        })}

        {/* Version pill — sits at the bottom of the nav, directly under
            System Admin (for admins) or under the last visible module
            (for everyone else). Traffic-light dot: green when this
            bundle IS the live deployed version, amber when a newer
            version has been deployed and the user needs to hard-refresh.
            Click → full revision history. Internal users only. */}
        {isInternal && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => handleNav("/revision-history")}
              className="mt-4 w-full flex items-center justify-between px-3 py-2 text-[11px] font-mono transition-colors rounded-sm border-l-2"
              style={isStale
                ? { backgroundColor: "#FEF3C7", color: "#92400E", borderLeftColor: "#D97706" }
                : location.pathname === "/revision-history"
                  ? { backgroundColor: "var(--theme-sidebar-active-bg)", color: "var(--theme-sidebar-active-text, var(--theme-primary, #0055FF))", borderLeftColor: "var(--theme-primary, #0055FF)" }
                  : { color: "var(--theme-sidebar-heading, #A3A3A3)", borderColor: "transparent" }}
              data-testid="nav-version-pill"
              data-version-state={isStale ? "stale" : "current"}
            >
              <span className="flex items-center gap-1.5">
                <span
                  className={`inline-block w-2 h-2 rounded-full ${isStale ? "bg-[#D97706] animate-pulse" : "bg-[#16A34A]"}`}
                  aria-hidden="true"
                />
                <span className="uppercase tracking-wider text-[10px]">Version</span>
              </span>
              <span className="font-bold font-mono" title={LATEST_RELEASE_LABEL}>{APP_VERSION}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="right" align="end" sideOffset={10}
            className="w-64 bg-[#0A0A0A] text-white border-[#0A0A0A] p-2.5 text-[11px] leading-snug"
          >
            {isStale ? (
              <>
                <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#FCD34D] mb-1">Update available — build {live?.version}</span>
                Your browser is running an older build. Press <kbd className="font-mono font-bold">Ctrl F5</kbd> (Windows) / <kbd className="font-mono font-bold">⌘ Shift R</kbd> (Mac) to hard-refresh and load the latest code. Then click here for the change list.
              </>
            ) : (
              <>
                <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">Build {APP_VERSION} &middot; {APP_RELEASE_DATE} &middot; up to date</span>
                Click to see the full revision history — release dates, version numbers, and what changed in each release.
              </>
            )}
          </TooltipContent>
        </Tooltip>
        )}

        {/* v1.57 — Live SQL Server + MongoDB connection info pill,
            sits directly under the Version pill. Diagnostic: shows the
            operator which database this pod is actually talking to.
            Click → /admin/db-connections for full diagnostics (login /
            connection-string echo removed in the 2026-08 hardening).
            Hidden until the API has responded. */}
        {isInternal && dbConn && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => handleNav("/admin/db-connections")}
                  className="mt-1 w-full px-3 py-2 text-[10px] font-mono leading-tight border-l-2 border-transparent hover:bg-[var(--theme-sidebar-active-bg,#F5F5F5)] transition-colors text-left"
                  style={{ color: "var(--theme-sidebar-heading, #A3A3A3)" }}
                  data-testid="nav-db-conn-pill"
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full ${dbConn?.sql_server?.connected ? "bg-[#16A34A]" : "bg-[#DC2626]"}`}
                      aria-hidden="true"
                    />
                    <span className="uppercase tracking-wider text-[9px]">SQL</span>
                    <span className="truncate" data-testid="sidebar-sql-host">
                      {dbConn?.sql_server?.host || "—"}
                    </span>
                  </div>
                  <div className="truncate pl-3" data-testid="sidebar-sql-db">
                    {dbConn?.sql_server?.database || "—"}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full bg-[#D4D4D4]`}
                      aria-hidden="true"
                      title="Unused — Pulse no longer reads or writes MongoDB"
                    />
                    <span className="uppercase tracking-wider text-[9px]">MONGO</span>
                    <span className="truncate italic text-[#A3A3A3]" data-testid="sidebar-mongo-host">
                      unused
                    </span>
                  </div>
                  <div className="truncate pl-3 italic text-[#A3A3A3]" data-testid="sidebar-mongo-db">
                    (cutover to SQL — Session 87)
                  </div>
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="right" align="end" sideOffset={10}
                className="max-w-md bg-[#0A0A0A] text-white border-[#0A0A0A] p-2.5 text-[10px] leading-snug font-mono break-all"
              >
                {/* 2026-08 incident hardening: the API no longer returns a
                    connection string — show host + database instead. */}
                <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">SQL Server</span>
                <span className="block mb-2">
                  {dbConn?.sql_server?.host
                    ? `${dbConn.sql_server.host}:${dbConn.sql_server.port || "1433"} / ${dbConn.sql_server.database || "—"}`
                    : "—"}
                </span>
                <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">MongoDB</span>
                <span className="block italic">Unused — Pulse cut over to SQL Server (Session 87). The Mongo connection probe is retained for diagnostics only.</span>
                <span className="block mt-2 text-[#A3A3A3]">Click to open full diagnostics →</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        </TooltipProvider>
      </nav>

      <div style={{ borderTop: "1px solid var(--theme-sidebar-border, #E5E5E5)" }} />

      <div className="p-4">
        {isAdmin && (
          <button
            onClick={() => handleNav("/team")}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors rounded-sm border-l-2 mb-3"
            style={location.pathname === "/team"
              ? { backgroundColor: "var(--theme-sidebar-active-bg)", color: "var(--theme-sidebar-active-text, var(--theme-primary, #0055FF))", borderLeftColor: "var(--theme-primary, #0055FF)" }
              : { color: "var(--theme-sidebar-text, #525252)", borderColor: "transparent" }}
            data-testid="nav-eyvo-team"
          >
            <UsersThree size={18} weight={location.pathname === "/team" ? "fill" : "regular"} />
            Eyvo Team
          </button>
        )}

        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center text-xs font-bold flex-shrink-0 overflow-hidden rounded-full" style={{ backgroundColor: "var(--theme-field-bg, #F5F5F5)", color: "var(--theme-sidebar-text, #525252)" }}>
            {user?.avatar ? (
              <img src={resolveBackendFileUrl(user.avatar)} alt="" className="w-full h-full object-cover" />
            ) : (
              user?.name?.charAt(0)?.toUpperCase() || "U"
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: "var(--theme-page-fg, #0A0A0A)" }}>{user?.name}</p>
            <p className="text-xs truncate" style={{ color: "var(--theme-sidebar-heading, #A3A3A3)" }}
               data-testid="sidebar-user-position-or-role">
              {/* Prefer the explicit `position` (e.g. "Marketing Lead") over
                  the legacy `role` column — the role column carries values
                  like "view_only" / "developer" which mis-represent what
                  the user can actually do (the per-module matrix is the
                  real source of truth). Fall back to a clean label when
                  position is empty. */}
              {(user?.position && user.position.trim())
                || (user?.user_type === "internal"
                      ? (user?.role === "platform_admin" ? "Platform Admin"
                         : user?.role === "delivery_admin" ? "Delivery Admin"
                         : "Team Member")
                      : (user?.role || "").replace(/_/g, " "))}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate("/profile")}
          className="w-full rounded-none border-[#E5E5E5] hover:border-[#0055FF] hover:text-[#0055FF] hover:bg-[#F0F7FF] text-xs font-bold uppercase tracking-wider h-9 mb-2" data-testid="profile-button">
          <Gear size={14} className="mr-2" /> My Profile
        </Button>
        <Button variant="outline" onClick={handleLogout}
          className="w-full rounded-none border-[#E5E5E5] hover:border-[#DC2626] hover:text-[#DC2626] hover:bg-[#FEF2F2] text-xs font-bold uppercase tracking-wider h-9" data-testid="logout-button">
          <SignOut size={14} className="mr-2" /> Sign Out
        </Button>
      </div>
    </div>
  );
}
